/**
 * Sync Outcomes Chart Component
 *
 * Grouped bar chart showing sync outcomes (synced, skipped, failed) by source
 */

import { Bar, BarChart, CartesianGrid, Legend, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { ChartCard, CHART_HEIGHT_CLASSES } from './ChartCard';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import type { SyncOutcomeBySource } from '@/types/dashboard';

interface SyncOutcomesChartProps {
  data: SyncOutcomeBySource[] | undefined;
  isLoading: boolean;
}

const SOURCE_LABELS: Record<string, string> = {
  fathom: 'Fathom',
  helpscout: 'HelpScout',
  basecamp: 'Basecamp',
};

const chartConfig = {
  synced: {
    label: 'Synced',
    color: 'hsl(var(--chart-success))',
  },
  skipped: {
    label: 'Skipped',
    color: 'hsl(var(--muted-foreground))',
  },
  failed: {
    label: 'Failed',
    color: 'hsl(var(--chart-error))',
  },
} satisfies ChartConfig;

export function SyncOutcomesChart({ data, isLoading }: SyncOutcomesChartProps) {
  // Transform data for recharts
  const chartData =
    data?.map((item) => ({
      source: SOURCE_LABELS[item.source] || item.source,
      synced: item.synced,
      skipped: item.skipped,
      failed: item.failed,
    })) ?? [];

  // Show skeleton when loading OR when data hasn't arrived yet (prop is undefined)
  const showSkeleton = isLoading || data === undefined;

  // Show "no data" only when data has loaded but is genuinely empty
  const isEmpty = !showSkeleton && chartData.length === 0;

  return (
    <ChartCard
      title="Sync Outcomes"
      tooltip={METRIC_DESCRIPTIONS.syncOutcomes}
      isLoading={showSkeleton}
      isEmpty={isEmpty}
      emptyMessage="No sync data available for the selected time range"
      emptyAriaLabel="No sync data available"
    >
      <ChartContainer
        config={chartConfig}
        className={`${CHART_HEIGHT_CLASSES} w-full`}
      >
        <BarChart data={chartData} accessibilityLayer>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="source"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            allowDecimals={false}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Legend />
          <Bar
            dataKey="synced"
            name="Synced"
            fill="var(--color-synced)"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="skipped"
            name="Skipped"
            fill="var(--color-skipped)"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="failed"
            name="Failed"
            fill="var(--color-failed)"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
